/**
 * 入札一覧の行に「取下」/「再入札」 を出すかを決める純関数 (AC-BSP-057 /
 * AC-BSP-081)。
 *
 * 取下を受け付けるかは backend の応答 (`withdrawable`) で決まる。 backend は
 * WithdrawSpotBid と同じ述語 (締切前・JEPX 到達済・約定待ち) で答えるので、 画面は
 * 承認軸 / 送信軸 / 約定軸の表を持たない。 画面は、 応答の後に入札締切を跨いだ場合と、
 * 確定済ワークフロー経由の読み取り専用 (backend の読取に概念が無い) だけを重ねて見る
 * (ブロック入札の `canWithdrawBlockBidGroup` と同じ)。
 *
 * 再入札 (AC-BSP-081) は新しい札を下書きから起票するだけで backend に述語が無い。
 * 取消済みの札で、 承認軸が提出済 (= 取消が板に出ていた札) の時に出す。
 *
 * 分岐を UI から切り離すのは、 締切後 / 取下処理中 / 取下失敗 のように画面操作では
 * 組みにくい状態まで含めて全分岐を F2 で固定するため。
 */
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingApprovalState,
  BiddingSpotBidStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { TFunction } from 'i18next'

import { convertAreaLabel } from '~/models/area'
import {
  type SpotBidRow,
  isMarketOrderRow,
} from '~/pages/SpotBidding/models/spotBidRow'

/** 行に出す取下系の操作。 */
export type SpotBidWithdrawAction =
  /** 取下 (AC-BSP-057)。 入札中の札にだけ出す。 */
  | 'withdraw'
  /** 再入札 (AC-BSP-081)。 取消済みの札にだけ出す。 */
  | 'rebid'
  /** どちらも出さない。 */
  | 'none'

/**
 * 行に出す取下系の操作を決める。
 *
 * @param bid 対象の札。
 * @param isBeforeDeadline 入札締切前か。 締切後は取下も再入札も実行できない
 *   (AC-BSP-057 は締切前限定、 再入札は新しい札の作成なので AC-BSP-013 で
 *   受渡日ごと対象外になる)。
 * @param isWorkflowReadonly 確定済ワークフロー経由で画面が read-only か
 *   (AC-BSP-058 が backend でも拒否する経路)。
 */
export const resolveSpotBidWithdrawAction = ({
  bid,
  isBeforeDeadline,
  isWorkflowReadonly,
}: {
  bid: Pick<SpotBidRow, 'approvalState' | 'bidStatus' | 'withdrawable'>
  isBeforeDeadline: boolean
  isWorkflowReadonly: boolean
}): SpotBidWithdrawAction => {
  if (!isBeforeDeadline || isWorkflowReadonly) return 'none'
  if (bid.withdrawable === true) return 'withdraw'
  // 取消の札が承認軸 SUBMITTED なのは、 送信軸が取消成立時点の値のまま残るため
  // (板に出ていたかは再入札に関係しない)。
  if (
    bid.bidStatus === BiddingSpotBidStatus.CANCELLED &&
    bid.approvalState === BiddingApprovalState.SUBMITTED
  ) {
    return 'rebid'
  }
  return 'none'
}

/**
 * 札のサマリの文言を、 成行札かどうかで引き分けて組み立てる。 成行札は価格を持たない
 * (内部表現の 0 を「0.00 円/kWh」 と出すと 0 円の指値に見える) ので、 一覧
 * (`SpotBidTable`) の「成行」 表示に揃える。
 */
const formatSpotBidSummary = (
  bid: SpotBidRow,
  t: TFunction,
  keys: { limit: string; market: string },
): string =>
  t(isMarketOrderRow(bid) ? keys.market : keys.limit, {
    koma: bid.timeCode,
    area: convertAreaLabel(bid.areaCode),
    side:
      bid.direction === Side.BUY
        ? t('common:tradeSide.buy')
        : t('common:tradeSide.sell'),
    price: Number(bid.priceYenPerKwh).toFixed(2),
    // 一覧・フォームとも数量は MW (AC-BSP-053) なので換算しない。
    volume: Number(bid.volumeMwh),
  })

/** 取下確認モーダルに出す札のサマリ (AC-BSP-057)。 */
export const formatSpotBidWithdrawSummary = (
  bid: SpotBidRow,
  t: TFunction,
): string =>
  formatSpotBidSummary(bid, t, {
    limit: 'page:biddingSpot.withdrawConfirm.body',
    market: 'page:biddingSpot.withdrawConfirm.bodyMarket',
  })

/** 下書きの札の 1 件削除確認に出す札のサマリ (AC-BSP-139)。 */
export const formatSpotBidDeleteSummary = (
  bid: SpotBidRow,
  t: TFunction,
): string =>
  formatSpotBidSummary(bid, t, {
    limit: 'page:biddingSpot.modal.deleteBidBody',
    market: 'page:biddingSpot.modal.deleteBidBodyMarket',
  })

/** 取下失敗のトーストに出す札のサマリ (AC-BSP-057b)。 */
export const formatSpotBidWithdrawFailureSummary = (
  bid: SpotBidRow,
  t: TFunction,
): string =>
  formatSpotBidSummary(bid, t, {
    limit: 'page:biddingSpot.withdrawFailedToast.summary',
    market: 'page:biddingSpot.withdrawFailedToast.summaryMarket',
  })

/**
 * 取下確認モーダルに出す「入札締切まで残り HH:MM」 (AC-BSP-057)。
 *
 * 締切に到達済み / 過ぎている場合は `00:00` を返す (負の残り時間を出さない)。
 * 60 時間先でも `60:00` のように時間を繰り上げず積み上げる (受渡日 2 日前から
 * 取り下げる運用があるため)。
 */
export const formatTimeUntilDeadline = (now: Date, deadline: Date): string => {
  const remainMs = deadline.getTime() - now.getTime()
  if (!Number.isFinite(remainMs) || remainMs <= 0) return '00:00'
  const totalMinutes = Math.floor(remainMs / 60_000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}
